/**
 * ETAM Filter Block - JavaScript
 * ==============================
 * Gestion des sélecteurs de filtres et construction de l'URL
 * Compatible SFCC Page Designer
 *
 * Utilise des <select> natifs pour une meilleure UX mobile
 *
 * Supporte 2 modes :
 * - HP (Home Page) : Utilise data-base-url pour rediriger vers une autre page
 * - LP (Landing Page) : Utilise l'URL actuelle (origin + pathname)
 *
 * Supporte la fusion des valeurs :
 * - Plusieurs sélecteurs peuvent partager la même filter-key
 * - Les valeurs sont automatiquement fusionnées avec | (encodé %7C dans l'URL)
 */

(function () {
  "use strict";

  // ================================
  // Configuration
  // ================================

  const CONFIG = {
    blockSelector: "#etamFilterBlock",
    selectorClass: ".etam-filter-block__selector",
    selectClass: ".etam-filter-block__select",
    ctaSelector: "#etamFilterCta",
    activeCtaClass: "etam-filter-block__cta--active",
    multiValueSeparator: "|",
  };

  // ================================
  // State Management
  // ================================

  class FilterBlockState {
    constructor(baseUrl = null, anchor = null) {
      // Stocke les sélections par selectorId (unique par sélecteur)
      // selectorId -> { values: [], filterKey: string, filterType: string }
      this.selectorSelections = new Map();
      this.baseUrl = baseUrl;
      this.anchor = anchor;
    }

    setSelection(selectorId, values, filterKey, filterType) {
      if (!Array.isArray(values)) {
        values = [values];
      }

      // Filtrer les valeurs vides
      values = values.filter((v) => v !== "" && v !== null && v !== undefined);

      if (values.length > 0) {
        this.selectorSelections.set(selectorId, { values, filterKey, filterType });
      } else {
        this.selectorSelections.delete(selectorId);
      }
    }

    getSelectionCount() {
      return this.selectorSelections.size;
    }

    buildUrl() {
      let url;

      // Déterminer l'URL de base
      if (this.baseUrl) {
        url = this.baseUrl.replace(/\/$/, "");
      } else {
        const origin = window.location.origin;
        const pathname = window.location.pathname.replace(/\/$/, "");
        url = origin + pathname;
      }

      // Regrouper les valeurs par filterKey et filterType
      const pathFilters = [];
      const paramFilters = new Map();

      this.selectorSelections.forEach((data) => {
        if (data.filterType === "path") {
          data.values.forEach((val) => pathFilters.push(val));
        } else if (data.filterType === "param") {
          if (!paramFilters.has(data.filterKey)) {
            paramFilters.set(data.filterKey, []);
          }
          paramFilters.get(data.filterKey).push(...data.values);
        }
      });

      // Ajouter les filtres de type path: /f/filter1/filter2/
      if (pathFilters.length > 0) {
        url += "/f/" + pathFilters.join("/") + "/";
      }

      // Ajouter les filtres de type param: ?prefn1=xxx&prefv1=xxx
      // Construction manuelle pour éviter le double encodage
      if (paramFilters.size > 0) {
        const paramParts = [];
        let index = 1;

        paramFilters.forEach((values, key) => {
          const joinedValue = values.join(CONFIG.multiValueSeparator);
          paramParts.push(`prefn${index}=${key}`);
          paramParts.push(`prefv${index}=${joinedValue}`);
          index++;
        });

        url += "?" + paramParts.join("&");
      }

      // Ajouter l'ancre
      if (this.anchor) {
        url += "#" + this.anchor;
      }

      return url;
    }
  }

  // ================================
  // Filter Block Controller
  // ================================

  class FilterBlockController {
    constructor(container) {
      this.container = container;
      this.selectors = [];
      this.cta = null;

      const baseUrl = container.dataset.baseUrl || null;
      const anchor = container.dataset.anchor || null;
      this.state = new FilterBlockState(baseUrl, anchor);

      this.init();
    }

    init() {
      this.cacheElements();
      this.assignSelectorIds();
      this.bindEvents();
    }

    cacheElements() {
      this.selectors = Array.from(
        this.container.querySelectorAll(CONFIG.selectorClass)
      );
      this.cta = this.container.querySelector(CONFIG.ctaSelector);
    }

    assignSelectorIds() {
      this.selectors.forEach((selector, index) => {
        if (!selector.dataset.selectorId) {
          selector.dataset.selectorId = `selector-${index}`;
        }
      });
    }

    bindEvents() {
      this.selectors.forEach((selector) => {
        const select = selector.querySelector(CONFIG.selectClass);

        if (select) {
          select.addEventListener("change", () => {
            this.handleSelectChange(selector, select);
          });
        }
      });
    }

    handleSelectChange(selector, select) {
      const selectorId = selector.dataset.selectorId;
      const filterType = selector.dataset.filterType;
      const filterKey = selector.dataset.filterKey;
      const value = select.value;

      // Mettre à jour le state
      this.state.setSelection(selectorId, [value], filterKey, filterType);

      // Mettre à jour le CTA
      this.updateCta();
    }

    updateCta() {
      if (!this.cta) return;

      const selectionCount = this.state.getSelectionCount();

      if (selectionCount >= 1) {
        this.cta.classList.add(CONFIG.activeCtaClass);
        this.cta.href = this.state.buildUrl();
      } else {
        this.cta.classList.remove(CONFIG.activeCtaClass);
        this.cta.href = "#";
      }
    }
  }

  // ================================
  // Initialization
  // ================================

  function initFilterBlocks() {
    const blocks = document.querySelectorAll(CONFIG.blockSelector);

    blocks.forEach((block) => {
      if (block.dataset.initialized) return;

      new FilterBlockController(block);
      block.dataset.initialized = "true";
    });
  }

  // Run on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initFilterBlocks);
  } else {
    initFilterBlocks();
  }

  // Expose for potential external use
  window.EtamFilterBlock = {
    init: initFilterBlocks,
    Controller: FilterBlockController,
    State: FilterBlockState,
  };
})();
